import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Clock, CheckCircle, Zap, Calendar, FileText, Palette, Smartphone, AlertCircle, Globe, RefreshCw, Search, Share2, PieChart, Layers, BarChart, Award, Target, Compass, Loader2, ExternalLink, ChevronRight, ChevronLeft, ListChecks, LogOut } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import BookAppQuestionnaire from './BookAppQuestionnaire';
import BrandfetchDisplay from './BrandfetchDisplay';
import { isLondonBookFairActive } from '../utils/dateUtils';
import '../styles/LondonBookFairDashboard.css';
import AppMockup from './AppMockup';
import RequirementsQuestionnaire from './RequirementsQuestionnaire';
import { ErrorBoundary } from 'react-error-boundary';

// Custom ErrorBoundary fallback component
const ErrorFallback = ({ error, resetErrorBoundary }) => (
  <div className="bg-green-800 text-white p-6 rounded-lg">
    <h3 className="text-xl font-semibold mb-4">Something went wrong</h3>
    <p className="mb-4">We encountered an error while loading the questionnaire.</p>
    <pre className="bg-green-900/50 p-3 rounded my-4 text-sm overflow-auto">
      {error.message}
    </pre>
    <button 
      onClick={resetErrorBoundary} 
      className="px-4 py-2 bg-white text-green-800 rounded-md hover:bg-gray-100 transition-colors"
    >
      Try Again
    </button>
  </div>
);

// Helper function to get a company logo URL using Clearbit
const getCompanyLogo = (domain) => {
    if (!domain) return null;
    
    // Extract domain name from URL if needed
    let cleanDomain = domain;
    if (domain.startsWith('http')) {
        try {
            const url = new URL(domain);
            cleanDomain = url.hostname;
        } catch (e) {
            // If URL parsing fails, use the original domain
            console.error('Error parsing domain URL:', e);
        }
    }
    
    // Remove www. prefix if present
    cleanDomain = cleanDomain.replace(/^www\./, '');
    
    // Return Clearbit URL for the domain
    return `https://logo.clearbit.com/${cleanDomain}`;
};

// This function should get data from /api/brand-data/by-user endpoints instead
const getSocialMediaProfiles = async (url) => {
    try {
        // Do not call APIs directly - social media profiles should come from brand-data endpoints
        console.log('Social media profiles should come from /api/brand-data/by-user endpoint');
        
        // Return empty object - in real implementation, these would come from brand data
        return {
            twitter: null,
            facebook: null,
            linkedin: null,
            instagram: null
        };
    } catch (error) {
        console.error('Error handling social media profiles:', error);
        return null;
    }
};

// This function should get data from /api/brand-data/by-user endpoints instead
const getExistingApps = async (companyName) => {
    try {
        // Do not call APIs directly - app information should come from brand-data endpoints
        console.log('App information should come from /api/brand-data/by-user endpoint');
        
        // Return empty array - in real implementation, these would come from brand data
        return [];
    } catch (error) {
        console.error('Error handling app information:', error);
        return null;
    }
};

const LondonBookFairDashboard = ({ user: propUser, organization: propOrganization }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showRequirementsForm, setShowRequirementsForm] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [submittedData, setSubmittedData] = useState(null);
    const [websiteData, setWebsiteData] = useState(null);
    const [isLoadingWebsiteData, setIsLoadingWebsiteData] = useState(false);
    const [websiteDataError, setWebsiteDataError] = useState(null);
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [aiData, setAiData] = useState(null);
    const [socialProfiles, setSocialProfiles] = useState(null);
    const [existingApps, setExistingApps] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [logoUrl, setLogoUrl] = useState(null);
    const [showQuestionnaire, setShowQuestionnaire] = useState(false);
    const [showUrlForm, setShowUrlForm] = useState(false);
    const [organizationWebsiteUrl, setOrganizationWebsiteUrl] = useState('');
    const [analysisStage, setAnalysisStage] = useState(0);
    const [analysisProgress, setAnalysisProgress] = useState(0);
    const [brandInfoExpanded, setBrandInfoExpanded] = useState(false);
    const [showBrandfetchDisplay, setShowBrandfetchDisplay] = useState(false);
    const [currentStage, setCurrentStage] = useState('input'); // 'input', 'analysis', 'mockup'
    const [loadingStage, setLoadingStage] = useState('');
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [pageTurnAnimation, setPageTurnAnimation] = useState(false);
    const [showFullQuestionnaire, setShowFullQuestionnaire] = useState(false);
    const [showCompletionOptions, setShowCompletionOptions] = useState(false);
    const appMockupRef = useRef(null);
    
    // Check for user and organization data in location state
    const locationUser = location.state?.userData;
    const locationOrganization = location.state?.organizationData;
    
    // Use either props or location state
    const user = propUser || locationUser;
    const organization = propOrganization || locationOrganization;
    
    // Steps in the process
    const steps = [
        { 
            icon: <Clock className="w-6 h-6 text-green-600" />, 
            title: "2 Minutes", 
            description: "Answer 8 quick questions about your requirements" 
        },
        { 
            icon: <Palette className="w-6 h-6 text-green-600" />, 
            title: "Brand Identity", 
            description: "Share your logo and color preferences" 
        },
        { 
            icon: <Zap className="w-6 h-6 text-green-600" />, 
            title: "7 Days", 
            description: "Receive your working prototype eBook & audiobook app" 
        },
        { 
            icon: <Smartphone className="w-6 h-6 text-green-600" />, 
            title: "Launch Ready", 
            description: "Your branded app ready for your content" 
        },
        {
            icon: <FileText className="w-6 h-6 text-green-600" />,
            title: "Audiobooks", 
            description: "Full-featured audiobook support with bookmarking and speed controls"
        },
        {
            icon: <Target className="w-6 h-6 text-green-600" />,
            title: "Online Courses", 
            description: "Expand your app with multimedia learning experiences"
        }
    ];

    // Features from the promotional image
    const features = [
        { 
            letter: "F", 
            title: "FRICTIONLESS SETUP", 
            description: "Launch your branded app in just one week" 
        },
        { 
            letter: "U", 
            title: "UNIVERSAL ACCESS", 
            description: "Comply with EAA obligations with leading accessibility" 
        },
        { 
            letter: "T", 
            title: "TURNKEY MONETISATION", 
            description: "In-app, web and subscription payments from launch" 
        },
        { 
            letter: "U", 
            title: "UNLIMITED POSSIBILITIES", 
            description: "Expand beyond eBooks to multimedia learning" 
        },
        { 
            letter: "R", 
            title: "RELEASE ON YOUR TERMS", 
            description: "Full control over content scheduling and updates" 
        },
        { 
            letter: "E", 
            title: "ECOSYSTEM FRIENDLY", 
            description: "Integrates with existing platforms and tools" 
        }
    ];

    // Analysis stages
    const analysisStages = [
        { name: "Analyzing website content", icon: <Search className="w-6 h-6 text-yellow-400 animate-pulse" /> },
        { name: "Fetching brand information", icon: <Globe className="w-6 h-6 text-blue-400 animate-pulse" /> },
        { name: "Generating color schemes", icon: <Palette className="w-6 h-6 text-green-400 animate-pulse" /> },
        { name: "Creating content categories", icon: <Layers className="w-6 h-6 text-purple-400 animate-pulse" /> },
        { name: "Finalizing recommendations", icon: <CheckCircle className="w-6 h-6 text-yellow-400 animate-pulse" /> }
    ];

    // Check if the London Book Fair is active
    useEffect(() => {
        if (!isLondonBookFairActive()) {
            navigate('/dashboard');
        }
    }, [navigate]);

    // Function to fetch existing data
    const fetchExistingData = async () => {
        if (organization?.id) {
            try {
                setIsLoading(true);
                
                // Set a basic organization website if available
                if (organization.website_url) {
                    setWebsiteUrl(organization.website_url);
                    setLogoUrl(getCompanyLogo(organization.website_url));
                }
                
                // Instead of trying to fetch website data that causes errors,
                // just provide basic information and direct users to the App Builder
                const basicData = {
                    title: organization.name || "Your Organization",
                    description: "To view detailed brand analysis and create an app, please use the App Builder.",
                    websiteUrl: organization.website_url || "",
                    suggestedAppName: `${organization.name || "Book"} App`
                };
                
                setAiData(basicData);
                
                // Add a clear message and button to direct users to the App Builder
                setWebsiteDataError(
                    <div className="flex flex-col items-center">
                        <p className="mb-3">For complete brand analysis including colors, logos, and social profiles, please use the App Builder wizard.</p>
                        <button 
                            onClick={handleGetStartedClick}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                            Go to App Builder
                        </button>
                    </div>
                );
                
                // Fetch social profiles and existing apps if website URL is available
                if (organization.website_url) {
                    fetchAdditionalData(organization.website_url, organization.name || '');
                }
                
            } catch (error) {
                console.error('Error in fetchExistingData:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchExistingData();
    }, [organization, location.state]);

    const fetchAdditionalData = async (url, companyName) => {
        try {
            console.warn('Additional data (social profiles, existing apps) should be fetched from /api/brand-data/by-user endpoint');
            
            // These functions now include warnings and don't make real API calls
            const profiles = await getSocialMediaProfiles(url);
            if (profiles) {
                setSocialProfiles(profiles);
            }
            
            const apps = await getExistingApps(companyName || '');
            if (apps) {
                setExistingApps(apps);
            }
        } catch (error) {
            console.error('Error with additional data:', error);
        }
    };

    // Function to refresh the AI content
    const refreshAiContent = () => {
        // Just re-fetch existing data instead of generating AI content
        fetchExistingData();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Just update the URL in state and redirect users to the App Builder
        if (websiteUrl) {
            // Instead of fetching website data, encourage users to use the App Builder
            setWebsiteDataError(
                <div className="flex flex-col items-center">
                    <p className="mb-3">To analyze your website and create a branded app, please use our App Builder wizard.</p>
                    <button 
                        onClick={handleGetStartedClick}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                        Go to App Builder
                    </button>
                </div>
            );
        }
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const handleFormSubmit = (formData) => {
        // Here you would normally send the data to your backend
        console.log('Form submitted with data:', formData);
        
        // If we have website data, use the AI-suggested colors
        if (aiData?.colorSchemes?.length > 0) {
            // Use the first color scheme from the AI suggestions
            const suggestedColors = aiData.colorSchemes[0];
            
            // Only override if the user didn't explicitly choose colors
            if (formData.brandColors.primary === '#4CAF50' && formData.brandColors.secondary === '#FFC107') {
                formData.brandColors = {
                    primary: suggestedColors.primary,
                    secondary: suggestedColors.secondary
                };
            }
        }
        
        // Store the submitted data
        setSubmittedData(formData);
        
        // Close the form and show success message
        setShowRequirementsForm(false);
        setShowSuccessMessage(true);
        
        // You could also send an email notification or create a task in your system
    };

    // Prepare app suggestions based on AI analysis
    const getAppSuggestions = () => {
        if (!aiData || !aiData.analysis) return null;
        
        return {
            appName: aiData.suggestedAppName || 'Your eBook App',
            colorSchemes: aiData.colorSchemes || [],
            appIdeas: aiData.appIdeas || [],
            contentCategories: aiData.contentCategories || []
        };
    };
    
    const appSuggestions = getAppSuggestions();

    // Update the loading animation
    useEffect(() => {
        if (isLoading) {
            // Reset progress
            setAnalysisProgress(0);
            setAnalysisStage(0);
            
            // Stage 1: Analyzing website content (0-20%)
            const timer1 = setTimeout(() => {
                setAnalysisProgress(20);
                setAnalysisStage(1);
            }, 1500);
            
            // Stage 2: Fetching brand information (20-40%)
            const timer2 = setTimeout(() => {
                setAnalysisProgress(40);
                setAnalysisStage(2);
            }, 3000);
            
            // Stage 3: Generating color schemes (40-60%)
            const timer3 = setTimeout(() => {
                setAnalysisProgress(60);
                setAnalysisStage(3);
            }, 4500);
            
            // Stage 4: Creating content categories (60-80%)
            const timer4 = setTimeout(() => {
                setAnalysisProgress(80);
                setAnalysisStage(4);
            }, 6000);
            
            // Stage 5: Finalizing recommendations (80-100%)
            const timer5 = setTimeout(() => {
                setAnalysisProgress(100);
            }, 7500);
            
            return () => {
                clearTimeout(timer1);
                clearTimeout(timer2);
                clearTimeout(timer3);
                clearTimeout(timer4);
                clearTimeout(timer5);
            };
        }
    }, [isLoading]);

    // New function to get brand info from aiData
    const getBrandInfo = () => {
        if (!aiData || !aiData.brandInfo) return null;
        
        return {
            name: aiData.brandInfo.name || 'Unknown Brand',
            description: aiData.brandInfo.description || 'No description available',
            colors: aiData.brandInfo.colors || [],
            fonts: aiData.brandInfo.fonts || [],
            logos: aiData.brandInfo.logos || [],
            links: aiData.brandInfo.links || []
        };
    };
    
    const brandInfo = getBrandInfo();

    // Add a function to handle stage navigation
    const goToNextStage = () => {
        if (currentStage === 'input') {
            setCurrentStage('analysis');
        } else if (currentStage === 'analysis') {
            setPageTurnAnimation(true);
            setTimeout(() => {
                setCurrentStage('mockup');
                setPageTurnAnimation(false);
            }, 1500);
        } else if (currentStage === 'mockup') {
            setPageTurnAnimation(true);
            setTimeout(() => {
                setCurrentStage('analysis');
                setPageTurnAnimation(false);
            }, 1500);
        }
    };

    const goToPreviousStage = () => {
        if (currentStage === 'mockup') {
            setPageTurnAnimation(true);
            setTimeout(() => {
                setCurrentStage('analysis');
                setPageTurnAnimation(false);
            }, 1500);
        } else if (currentStage === 'analysis') {
            setCurrentStage('input');
        }
    };

    // Handle user logout
    const handleLogout = async () => {
        try {
            // Make a server-side logout request
            const response = await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include'
            });
            
            // Clear user data from localStorage
            localStorage.removeItem('userData');
            
            // Also clear app demo state to prevent persistence between users
            localStorage.removeItem('appDemoState');
            
            // Clear saved material palettes to prevent accumulation across user sessions
            localStorage.removeItem('savedMaterialPalettes');
            
            // Redirect to home page instead of just reloading
            window.location.href = '/';
        } catch (error) {
            console.error('Logout failed:', error);
            
            // Even if server logout fails, clear client-side state and redirect
            localStorage.removeItem('userData');
            localStorage.removeItem('appDemoState');
            localStorage.removeItem('savedMaterialPalettes');
            window.location.href = '/';
        }
    };

    // New function to handle the CTA button click
    const handleGetStartedClick = () => {
        if (user) {
            // If already logged in, go directly to app wizard
            navigate('/app-wizard');
        } else {
            // If not logged in, go to registration page
            navigate('/register-lbf');
        }
    };

    // New function to handle the questionnaire button click
    const handleQuestionnaireClick = () => {
        // Show loading indicator
        setIsLoading(true);
        
        if (user) {
            // If user is logged in, show the questionnaire directly
            setShowQuestionnaire(true);
            setIsLoading(false);
        } else {
            // If not logged in, go to registration page
            // Store intent in localStorage so we can redirect to questionnaire after registration
            localStorage.setItem('lbf_redirect_intent', 'questionnaire');
            navigate('/register-lbf');
        }
    };

    // Update the loading indicator state in the UI
    useEffect(() => {
        // If we're showing the homepage but not the questionnaire, 
        // make sure loading state is cleared after a short delay
        if (!showQuestionnaire && currentStage === 'input') {
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 1000);
            
            return () => clearTimeout(timer);
        }
    }, [showQuestionnaire, currentStage]);

    // Check if we're coming back from registration with a flag to show the questionnaire
    useEffect(() => {
        if (location.state?.showQuestionnaire && user) {
            setShowQuestionnaire(true);
            
            // Clear the state to prevent showing questionnaire on refresh
            window.history.replaceState({}, document.title);
        }
        
        if (location.state?.showFullQuestionnaire && user) {
            setShowFullQuestionnaire(true);
            
            // Clear the state to prevent showing questionnaire on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location.state, user]);
    
    // Add a new effect to handle the case when user logs in and there's a redirect intent
    useEffect(() => {
        // This effect runs when user logs in
        if (user) {
            const redirectIntent = localStorage.getItem('lbf_redirect_intent');
            
            if (redirectIntent === 'questionnaire') {
                localStorage.removeItem('lbf_redirect_intent');
                setShowQuestionnaire(true);
            } else if (redirectIntent === 'fullQuestionnaire') {
                localStorage.removeItem('lbf_redirect_intent');
                setShowFullQuestionnaire(true);
            }
        }
    }, [user]);

    // Add a function to handle the requirements questionnaire button click
    const handleRequirementsQuestionnaireClick = () => {
        // Show loading indicator
        setIsLoading(true);
        
        if (user) {
            // If user is logged in, show the full questionnaire directly
            setShowFullQuestionnaire(true);
            setIsLoading(false);
        } else {
            // If not logged in, go to registration page
            // Store intent in localStorage so we can redirect to questionnaire after registration
            localStorage.setItem('lbf_redirect_intent', 'fullQuestionnaire');
            navigate('/register-lbf');
        }
    };

    return (
        <div className="min-h-screen h-screen bg-green-800 text-white flex flex-col overflow-hidden">
            {/* Simple top header with user info if logged in */}
            {user && (
                <div className="bg-green-900 p-2 flex justify-end items-center">
                    <div className="flex items-center space-x-3">
                        {logoUrl && (
                            <img src={logoUrl} alt="Organization logo" className="h-6 w-6 rounded-full" />
                        )}
                        {user?.email && (
                            <span className="text-white text-sm">{user.email}</span>
                        )}
                        <button
                            onClick={handleLogout}
                            className="text-white px-3 py-1 bg-green-700 hover:bg-green-600 rounded-md text-sm flex items-center"
                        >
                            Logout
                        </button>
                            </div>
                        </div>
            )}
            
            {/* Main content area with constrained height */}
            <div className="flex-1 p-2 sm:p-3 overflow-hidden">
                <div className="max-w-7xl mx-auto h-full">
                    {/* Two-column layout for all content */}
                    <div className="flex flex-col lg:flex-row gap-6 h-full">
                    {/* Left Column - Eden Interactive Branding */}
                        <div className="lg:w-2/5 h-full">
                            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 shadow-xl h-full overflow-auto">
                            <div className="flex items-center mb-6">
                                <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center mr-3">
                                    <span className="text-green-700 font-bold text-xl">ei</span>
                                </div>
                                <div>
                                    <h2 className="text-white font-bold text-xl">eden interactive</h2>
                                    <p className="text-white/80 text-sm">the beginning of something good</p>
                                </div>
                            </div>
                            
                                <h3 className="text-white text-xl mb-2">The <span className="text-yellow-300 font-bold">FUTURE</span> of white label</h3>
                            <h3 className="text-white text-xl mb-6">eBook and Audio apps</h3>
                            
                                {/* FUTURE Features - Improved contrast */}
                            <div className="space-y-6">
                                {features.map((feature, index) => (
                                    <div key={index} className="flex">
                                            <div className="bg-green-600/40 rounded-lg w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
                                                <span className="text-yellow-300 font-bold text-2xl">{feature.letter}</span>
                                        </div>
                                        <div>
                                                <h4 className="text-yellow-300 font-bold">{feature.title}</h4>
                                                <p className="text-white text-sm">{feature.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                                <div className="mt-6 text-center">
                                    <a 
                                        href="https://www.edeninteractive.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="text-white/80 text-sm hover:text-white transition-colors inline-flex items-center"
                                    >
                                    www.edeninteractive.com
                                        <ExternalLink className="w-3 h-3 ml-1" />
                                </a>
                            </div>
                        </div>
                    </div>
                    
                        {/* Right Column - Special Offers and Content */}
                        <div className="lg:w-3/5 flex flex-col h-full">
                            <div className="flex flex-col h-full overflow-hidden">
                                {/* London Book Fair 2025 Banner */}
                                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 mb-4 border border-white/20 shadow-xl">
                                    <div className="flex flex-col md:flex-row items-center justify-between">
                                        <div className="mb-4 md:mb-0">
                                            <div className="flex items-center">
                                                <Calendar className="w-6 h-6 text-white mr-2" />
                                                <h3 className="text-white font-semibold">London Book Fair 2025 Special Offer</h3>
                                            </div>
                                            <p className="text-white/80 mt-1">Valid until March 30, 2025</p>
                                        </div>
                                        <div>
                                            <span className="inline-block bg-yellow-500 text-green-900 font-bold px-4 py-2 rounded-full text-sm">
                                                NO UPFRONT COST & NO MINIMUM CONTRACT
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Special SHOW OFFER Card */}
                                <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 shadow-xl flex-1 overflow-auto">
                                    <h2 className="text-2xl font-bold mb-4">
                                        <span className="text-yellow-300">Special</span> SHOW OFFER
                            </h2>
                                    <p className="text-white mb-6">
                                Sign up within 60 days of today for NO UPFRONT COST & NO MINIMUM CONTRACT
                            </p>
                            
                                    {/* Process Steps - Removed green background */}
                                    <div className="mb-6 border-b border-white/20 pb-6">
                                        <h3 className="text-white font-semibold mb-4">How it works:</h3>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {steps.map((step, index) => (
                                        <div key={index} className="flex items-start">
                                                    <div className="bg-white/10 rounded-full p-3 mr-3 flex-shrink-0">
                                                        {React.cloneElement(step.icon, { className: "w-6 h-6 text-yellow-300" })}
                                            </div>
                                            <div>
                                                        <h4 className="text-yellow-300 font-semibold">{step.title}</h4>
                                                        <p className="text-white text-sm">{step.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                                    {/* Combined Preview Section and CTA - Removed green background */}
                                <div className="mb-6">
                                        <h3 className="text-white font-semibold mb-4">Preview Your White Label eBook Platform</h3>
                                        <div className="flex flex-col space-y-6">
                                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                                <button
                                                    onClick={handleQuestionnaireClick}
                                                    className="bg-white hover:bg-gray-100 text-green-800 font-bold py-3 px-8 rounded-lg inline-flex items-center transition-colors shadow-lg"
                                                >
                                                    <Zap className="w-5 h-5 mr-2" />
                                                    Quick Questions
                                                </button>
                                                
                                                <button
                                                    onClick={handleGetStartedClick}
                                                    className="bg-yellow-500 hover:bg-yellow-400 text-green-800 font-bold py-3 px-8 rounded-lg inline-flex items-center transition-colors shadow-lg"
                                                >
                                                    <ArrowRight className="w-5 h-5 mr-2" />
                                                    Build App Now
                                                </button>
                                            </div>
                                            
                                            <div className="text-center">
                                                <p className="text-white/80 text-sm mt-2">
                                                    No commitment required - create your branded app in minutes
                                                </p>
                                            </div>
                                </div>
                                                </div>
                                            
                                    {/* ... existing code for aiData display, etc. ... */}
                                                </div>
                                            </div>
                                                            </div>
                                                    </div>
                                                        </div>
                                                </div>
            
            {/* Registration Form Modal - rendered conditionally when needed */}
                {showRequirementsForm && (
                                <BookAppQuestionnaire 
                    aiData={aiData || {}}
                    organization={organization}
                    lbfMode={true}
                    onBack={() => setShowRequirementsForm(false)}
                />
            )}
            
            {/* Brandfetch Display Modal */}
            {showBrandfetchDisplay && brandInfo && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <BrandfetchDisplay 
                        brandData={brandInfo} 
                        onClose={() => setShowBrandfetchDisplay(false)} 
                    />
                </div>
            )}
            
            {/* Success Message Modal */}
            {showSuccessMessage && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-2xl font-bold">Success!</h2>
                                <button 
                                    onClick={() => setShowSuccessMessage(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="text-center mb-6">
                                <div className="bg-green-100 rounded-full p-4 inline-block mb-4">
                                    <CheckCircle className="w-12 h-12 text-green-500" />
                                </div>
                                <h3 className="text-xl font-bold mb-2">Your Requirements Have Been Submitted</h3>
                                <p className="text-gray-600">
                                    Thank you for submitting your requirements. Our team will review your information and get back to you shortly.
                                </p>
                            </div>
                            <div className="bg-gray-100 rounded-lg p-4 mb-6">
                                <h4 className="font-bold mb-2">Next Steps:</h4>
                                <ol className="list-decimal list-inside text-gray-700">
                                    <li className="mb-1">Our team will review your requirements</li>
                                    <li className="mb-1">We'll prepare a prototype based on your needs</li>
                                    <li className="mb-1">You'll receive your working app within 7 days</li>
                                </ol>
                            </div>
                                <button
                                    onClick={() => setShowSuccessMessage(false)}
                                className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 transition-colors"
                            >
                                    Got it!
                                </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Questionnaire overlay */}
            {showQuestionnaire && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
                    <BookAppQuestionnaire 
                        aiData={aiData} 
                        organization={organization}
                        lbfMode={true}
                        user={user}
                        onBack={() => setShowQuestionnaire(false)} 
                    />
                </div>
            )}
            
            {/* Full Requirements Questionnaire overlay */}
            {showFullQuestionnaire && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
                    <div className="w-full max-w-5xl bg-green-800 border border-green-700 rounded-xl shadow-2xl overflow-hidden my-8">
                        <div className="p-4 flex items-center justify-between border-b border-green-700">
                            <h2 className="text-xl font-bold text-white">Requirements Questionnaire</h2>
                            <button 
                                onClick={() => setShowFullQuestionnaire(false)}
                                className="text-white/70 hover:text-white"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-4">
                            {!user ? (
                                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                                    <AlertCircle className="w-8 h-8 text-red-500" />
                                    <div className="text-lg text-white">You must be logged in to access the questionnaire</div>
                                    <button 
                                        onClick={() => setShowFullQuestionnaire(false)}
                                        className="mt-4 px-4 py-2 bg-white text-green-800 rounded-md hover:bg-gray-100 transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            ) : (
                                <ErrorBoundary 
                                    FallbackComponent={({ error, resetErrorBoundary }) => (
                                        <div className="bg-green-800 text-white p-6">
                                            <h3 className="text-xl font-semibold mb-4">Requirements Questionnaire</h3>
                                            <p className="mb-4">Thank you for registering! Our requirements questionnaire helps us understand your specific needs for your white label app.</p>
                                            
                                            <div className="bg-green-700/50 p-4 rounded-lg mb-6">
                                                <h4 className="font-semibold mb-2">We'll need to know about:</h4>
                                                <ul className="list-disc pl-5 space-y-2">
                                                    <li>Your target audience and their preferences</li>
                                                    <li>Content types you want to include in your app</li>
                                                    <li>Monetization strategies you're interested in</li>
                                                    <li>Branding requirements and design preferences</li>
                                                    <li>Technical requirements and integration needs</li>
                                                </ul>
                                            </div>
                                            
                                            <div className="bg-green-900/50 p-4 rounded-lg mb-4">
                                                <h4 className="font-semibold text-yellow-300 mb-2">Technical Issue</h4>
                                                <p className="mb-2">We encountered an error while loading the questionnaire:</p>
                                                <pre className="bg-green-900/70 p-3 rounded my-2 text-sm overflow-auto text-white/80">
                                                    {error.message}
                                                </pre>
                                            </div>
                                            
                                            <div className="flex justify-between mt-6">
                                                <button 
                                                    onClick={() => setShowFullQuestionnaire(false)}
                                                    className="px-4 py-2 bg-white text-green-800 rounded-md hover:bg-gray-100 transition-colors"
                                                >
                                                    Close
                                                </button>
                                                <button 
                                                    onClick={() => {
                                                        setShowFullQuestionnaire(false);
                                                        setShowCompletionOptions(true);
                                                    }}
                                                    className="px-4 py-2 bg-yellow-500 text-green-800 rounded-md hover:bg-yellow-400 transition-colors"
                                                >
                                                    Continue to App Builder
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    onReset={() => {
                                        // Reset any error state
                                        setError(null);
                                    }}
                                >
                                    <RequirementsQuestionnaire
                                        user={user}
                                        organization={organization || location.state?.organizationData || {
                                            id: 'temp-org-id',
                                            name: 'Your Organization',
                                            currency: 'USD'
                                        }}
                                        hideTeamSection={true}
                                        startInReviewMode={false}
                                        initialResponses={{}} // Empty object, responses will be loaded from API
                                        onComplete={() => {
                                            setShowFullQuestionnaire(false);
                                            setShowCompletionOptions(true);
                                        }}
                                    />
                                </ErrorBoundary>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Completion Options Dialog */}
            {showCompletionOptions && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-2xl font-bold">Questionnaire Completed</h2>
                                <button 
                                    onClick={() => setShowCompletionOptions(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="text-center mb-6">
                                <div className="bg-green-100 rounded-full p-4 inline-block mb-4">
                                    <CheckCircle className="w-12 h-12 text-green-500" />
                                </div>
                                <h3 className="text-xl font-bold mb-2">Thank You for Your Responses</h3>
                                <p className="text-gray-600">
                                    Your requirements have been saved. What would you like to do next?
                                </p>
                            </div>
                            <div className="flex flex-col gap-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowCompletionOptions(false);
                                        handleGetStartedClick(); // Navigate to app wizard
                                    }}
                                    className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                                >
                                    <Smartphone className="w-5 h-5 mr-2" />
                                    Build Working Prototype App
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="w-full py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
                                >
                                    <LogOut className="w-5 h-5 mr-2" />
                                    Logout
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Discreet link to regular dashboard */}
            <div className="fixed bottom-2 right-2 opacity-60 hover:opacity-100 transition-opacity z-10">
                <button 
                    onClick={() => navigate('/dashboard')} 
                    title="Go to Regular Dashboard"
                    className="flex items-center justify-center p-2 bg-green-900/50 backdrop-blur-sm rounded-full hover:bg-green-900 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default LondonBookFairDashboard; 